// Company structure — the nested tree (GET /company-structure → OrgUnit shape
// the tree/chart views render), single-node details, and unit creation.
// Member counts aren't part of the tree payload.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';
import type { OrgUnit } from '@/mocks/structure';
import type { Tag } from './tagsApi';

interface ApiNode {
  _id: string;
  name: string;
  location: string | null;
  referenceNumber?: number | null; // sequential node reference (ND-42)
  level: { _id: string; level: number; name: string };
  users?: unknown[] | null; // members placed on the node; length → memberCount
  tags?: { _id: string; title: string }[];
  childs?: ApiNode[];
}

const toOrgUnit = (n: ApiNode): OrgUnit => {
  const tags = (n.tags ?? []).map((t) => ({ id: t._id, title: t.title }));
  return {
    id: n._id,
    name: n.name,
    level: n.level?.level ?? 0,
    location: n.location ?? '',
    referenceNumber: n.referenceNumber ?? '',
    memberCount: (n.users ?? []).length, // members placed directly on the node
    tags,
    tag: tags[0]?.title, // first tag drives the single-chip display
    children: n.childs?.map(toOrgUnit),
  };
};

export type NodeStatus = 'active' | 'deactivated' | 'hidden';

export interface NodeDetails {
  id: string;
  name: string;
  description: string;
  location: string;
  googleMapLink: string;
  coordinates: string;
  /** Geofence radius around the coordinates, in meters; null when unset. */
  radius: number | null;
  level: number;
  referenceNumber: number | string;
  parentId: string;
  parentName: string;
  people: { id: string; name: string }[];
  peopleCount: number;
  status: NodeStatus;
  tags: Tag[];
}

// Details payload (confirmed): level/parent/users/tags arrive populated;
// `parent` is null/absent on the root node.
interface ApiUser {
  _id: string;
  userStatus: string;
  user: {
    firstName: string;
    lastName: string;
  };
}
interface ApiNodeDetails {
  _id: string;
  name: string;
  description?: string | null;
  location?: string | null;
  googleMapLink?: string | null;
  coordinates?: string | null;
  radius?: number | null;
  referenceNumber?: number | null;
  level: { _id: string; level: number; name: string };
  parent?: { _id: string; name: string } | null;
  users?: ApiUser[];
  peopleCount?: number | null;
  status?: NodeStatus | null;
  tags?: { _id: string; title: string }[];
}

const userName = (u: ApiUser): string => `${u.user.firstName} ${u.user.lastName}`.trim();

const toNodeDetails = (n: ApiNodeDetails): NodeDetails => {
  // `id` is the assignment `_id` — what update-node's addedUsers/removedUsers expect.
  const people = (n.users ?? []).map((u) => ({ id: u._id, name: userName(u) }));
  return {
    id: n._id,
    name: n.name,
    description: n.description ?? '',
    location: n.location ?? '',
    googleMapLink: n.googleMapLink ?? '',
    coordinates: n.coordinates ?? '',
    radius: n.radius ?? null,
    level: n.level.level,
    referenceNumber: n.referenceNumber ?? '',
    parentId: n.parent?._id ?? '',
    parentName: n.parent?.name ?? '',
    people,
    peopleCount: n.peopleCount ?? people.length,
    status: n.status ?? 'active',
    tags: (n.tags ?? []).map((t) => ({ id: t._id, title: t.title })),
  };
};

export interface AddNodePayload {
  name: string;
  description: string;
  parent: string;
  // Optional location fields are sent as null when left empty.
  location: string | null;
  googleMapLink: string | null;
  coordinates: string | null;
  // Radius in meters around the coordinates; null when the radius is off.
  radius: number | null;
  tags: string[];
}

// Update carries the full node fields (parent echoed unchanged — reparenting is
// a later flow) plus people changes as a diff: user ids newly placed / unplaced.
export type UpdateNodePayload = AddNodePayload & {
  addedUsers: string[];
  removedUsers: string[];
};

export const structureApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getCompanyStructure: build.query<OrgUnit[], void>({
      query: () => ({ ...ROUTES.structure.tree }),
      transformResponse: (res: ApiNode[]) => (res ?? []).map(toOrgUnit),
      providesTags: ['Structure'],
    }),
    // Same tree shape, scoped for selection UIs (pickers, filter dropdowns).
    getStructureSelection: build.query<OrgUnit[], void>({
      query: () => ({ ...ROUTES.structure.selection }),
      transformResponse: (res: ApiNode[]) => (res ?? []).map(toOrgUnit),
      providesTags: ['Structure'],
    }),
    getNodeDetails: build.query<NodeDetails, string>({
      query: (id) => ({ ...ROUTES.structure.nodeDetails(id) }),
      transformResponse: (res: ApiNodeDetails) => toNodeDetails(res),
      providesTags: (_res, _err, id) => [{ type: 'Structure' as const, id }],
    }),
    addNode: build.mutation<{ _id?: string }, AddNodePayload>({
      query: (body) => ({ ...ROUTES.structure.addNode, data: body }),
      invalidatesTags: ['Structure'],
    }),
    updateNode: build.mutation<unknown, { id: string; body: UpdateNodePayload }>({
      query: ({ id, body }) => ({ ...ROUTES.structure.updateNode(id), data: body }),
      // Refresh the tree (a renamed unit updates in place) and the open detail.
      invalidatesTags: (_res, _err, { id }) => ['Structure', { type: 'Structure', id }],
    }),
    updateNodesStatus: build.mutation<unknown, { nodeIds: string[]; status: NodeStatus }>({
      query: (body) => ({ ...ROUTES.structure.updateNodesStatus, data: body }),
      invalidatesTags: (_res, _err, { nodeIds }) => [
        'Structure',
        ...nodeIds.map((id) => ({ type: 'Structure' as const, id })),
      ],
    }),
    // Nodes at a given level that the current user can access (for pickers).
    getNodesByLevel: build.query<Array<{ id: string; name: string }>, string>({
      query: (levelId) => ({ ...ROUTES.structure.selectionByLevel, data: { levelId } }),
      transformResponse: (res: Array<{ _id: string; name?: string | null }>) =>
        (res ?? []).map((n) => ({ id: n._id, name: n.name ?? '' })),
    }),
  }),
});

export const {
  useGetCompanyStructureQuery,
  useGetStructureSelectionQuery,
  useGetNodeDetailsQuery,
  useAddNodeMutation,
  useUpdateNodeMutation,
  useUpdateNodesStatusMutation,
  useGetNodesByLevelQuery,
} = structureApi;
